import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export type SortDirection = "asc" | "desc";

interface SortableTHProps<K extends string> {
  sortKey: K;
  current: K;
  direction: SortDirection;
  onClick: (key: K) => void;
  align?: "left" | "right" | "center";
  children: ReactNode;
}

export function SortableTH<K extends string>({
  sortKey,
  current,
  direction,
  onClick,
  align = "left",
  children,
}: SortableTHProps<K>) {
  const isActive = sortKey === current;
  const alignClass =
    align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";

  const Arrow = !isActive
    ? ChevronsUpDown
    : direction === "asc"
      ? ChevronUp
      : ChevronDown;

  return (
    <TableHead
      scope="col"
      onClick={() => onClick(sortKey)}
      aria-sort={isActive ? (direction === "asc" ? "ascending" : "descending") : "none"}
      className={cn(
        "cursor-pointer select-none text-xs font-medium uppercase tracking-wide hover:text-foreground",
        align === "right" && "text-right",
        align === "center" && "text-center",
      )}
    >
      <span className={cn("inline-flex items-center gap-1", alignClass)}>
        {children}
        <Arrow
          className={cn("h-3.5 w-3.5", isActive ? "text-foreground" : "text-muted-foreground/40")}
        />
      </span>
    </TableHead>
  );
}

export function useSortableRows<T, K extends string>(
  rows: readonly T[],
  initialKey: K,
  initialDirection: SortDirection = "asc",
  getValue?: Partial<Record<K, (row: T) => string | number | null | undefined>>,
) {
  const [sortKey, setSortKey] = useState<K>(initialKey);
  const [direction, setDirection] = useState<SortDirection>(initialDirection);

  function setSort(key: K) {
    if (key === sortKey) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDirection("asc");
    }
  }

  const sortedRows = useMemo(
    () => sortRows(rows, sortKey, direction, getValue),
    [rows, sortKey, direction, getValue],
  );

  return { sortedRows, sortKey, direction, setSort };
}

export function sortRows<T, K extends string>(
  rows: readonly T[],
  sortKey: K,
  direction: SortDirection,
  getValue?: Partial<Record<K, (row: T) => string | number | null | undefined>>,
): T[] {
  const extract = getValue?.[sortKey];
  const fallback = (row: T) =>
    (row as Record<string, unknown>)[sortKey] as string | number | null | undefined;
  const get = extract ?? fallback;

  const copy = rows.slice();
  copy.sort((a, b) => {
    const av = get(a);
    const bv = get(b);

    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return direction === "asc" ? -1 : 1;
    if (av > bv) return direction === "asc" ? 1 : -1;
    return 0;
  });
  return copy;
}
